
import React, { createContext, useContext, useState, useEffect } from 'react';
import { SupportedLocale, DEFAULT_LOCALE, AVAILABLE_LOCALES } from './config';
import { translations as esTranslations } from './translations/es';
import { translations as enTranslations } from './translations/en';

type TranslationsType = typeof esTranslations;

const translationMap: Record<SupportedLocale, TranslationsType> = {
  es: esTranslations,
  en: enTranslations
};

interface I18nContextType {
  locale: SupportedLocale;
  setLocale: (locale: SupportedLocale) => void;
  t: TranslationsType;
}

const I18nContext = createContext<I18nContextType | undefined>(undefined);

export const useI18n = () => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  return context;
};

interface I18nProviderProps {
  children: React.ReactNode;
  initialLocale?: SupportedLocale;
}

export const I18nProvider: React.FC<I18nProviderProps> = ({
  children,
  initialLocale = DEFAULT_LOCALE,
}) => {
  const [locale, setLocale] = useState<SupportedLocale>(initialLocale);
  const [translations, setTranslations] = useState<TranslationsType>(translationMap[initialLocale]);

  useEffect(() => {
    // Update document language attribute
    document.documentElement.lang = locale;
    // Update translations
    setTranslations(translationMap[locale]);
  }, [locale]);

  const value = {
    locale,
    setLocale,
    t: translations,
  };

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};
